import { addDays, endOfWeek, startOfDay } from 'date-fns'

/**
 * Relative due-date buckets the Due filter offers (design 9a quick filters /
 * 9b). Stored in filters as these literal keys, so a saved "Overdue" view
 * stays relative to whenever it is opened.
 */
export const DUE_PRESETS = [
  'overdue',
  'today',
  'tomorrow',
  'this_week',
  'next_7_days',
  'no_date',
] as const

export type DuePreset = (typeof DUE_PRESETS)[number]

export const DUE_PRESET_LABELS: Record<DuePreset, string> = {
  overdue: 'Overdue',
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  next_7_days: 'Next 7 days',
  no_date: 'No due date',
}

/**
 * Every preset a due date falls into. Buckets overlap (a task due today is in
 * Today, This week and Next 7 days), which is what lets `includes` / `excludes`
 * reuse the select-filter machinery.
 */
export function getDuePresets(
  dueDate: number | null | undefined,
  now: number = Date.now(),
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1,
): Array<DuePreset> {
  if (dueDate == null) return ['no_date']

  const today = startOfDay(now).getTime()
  const tomorrow = addDays(today, 1).getTime()
  const dayAfter = addDays(today, 2).getTime()
  const weekEnd = endOfWeek(now, { weekStartsOn }).getTime()
  const sevenDaysOut = addDays(today, 8).getTime() // through the end of day +7

  const presets: Array<DuePreset> = []
  if (dueDate < today) presets.push('overdue')
  if (dueDate >= today && dueDate < tomorrow) presets.push('today')
  if (dueDate >= tomorrow && dueDate < dayAfter) presets.push('tomorrow')
  if (dueDate >= today && dueDate <= weekEnd) presets.push('this_week')
  if (dueDate >= today && dueDate < sevenDaysOut) presets.push('next_7_days')
  return presets
}
